import { useMutation, useQueryClient } from "@tanstack/react-query";
import * as React from "react";

import {
  ApiError,
  submitDeal,
  updateDealDraft,
  type Deal,
  type DealDraftUpdatePayload,
  type IndustryVertical,
  type ServiceOffering,
} from "@/api/client";
import { useCurrentUser } from "@/auth/useCurrentUser";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DateRowInput } from "@/components/ui/date-row-input";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FormError } from "@/components/ui/form-error";
import { formatAud } from "@/lib/currency";

import { decode422, highlightFieldFor, joinName, splitName, stepForField } from "./deal422";

interface Props {
  deal: Deal;
  dealId: string;
  onSubmit: () => void;
  onBack: () => void;
  /** Lift a saved in-place edit back to the wizard's deal state. */
  onDealChange: (deal: Deal) => void;
  /** Wizard position (CHP-368: 4 when an Assign-Deal-Owner step precedes the
   * flow; 3 otherwise). Drives the card heading. */
  stepNumber?: number;
  /** CHP-809 / Flow B "Partner & Owner Identity": on the on-behalf (CM/RA/RM)
   * path, the actor verifies who the deal is registered to before submit. The
   * org follows the chosen assignee's profile server-side and the owner was
   * picked on a prior step, so this is a read-only verify block (not editable
   * from here). Omitted on partner self-registration. */
  ownerIdentity?: { orgName: string | null; ownerName: string | null };
  /** CHP-474 cross-step jump: when a submit 422 names a field owned by an
   * earlier step (e.g. an Opportunity field), bubble it up so the wizard can
   * navigate to that step and highlight the offending input. */
  onFieldError?: (field: string) => void;
}

const INDUSTRY_OPTIONS: { value: IndustryVertical; label: string }[] = [
  { value: "healthcare", label: "Healthcare" },
  { value: "finance", label: "Finance" },
  { value: "utilities", label: "Utilities" },
  { value: "logistics", label: "Logistics" },
  { value: "government", label: "Government" },
  { value: "technology", label: "Technology" },
  { value: "retail", label: "Retail" },
];

const INDUSTRY_LABELS: Record<string, string> = Object.fromEntries(
  INDUSTRY_OPTIONS.map((o) => [o.value, o.label]),
);

const SERVICE_OPTIONS: { value: ServiceOffering; label: string }[] = [
  { value: "BMaaS", label: "BMaaS (Bare Metal as a Service)" },
];

const TERM_OPTIONS = [12, 24, 36, 48, 60] as const;

function centsToDollars(cents: number | null): string {
  if (cents == null) return "";
  return (cents / 100).toFixed(2);
}

function dollarsToCents(dollars: string): number | null {
  const n = Number(dollars);
  if (!Number.isFinite(n) || n < 0) return null;
  return Math.round(n * 100);
}

function isValidAbn(value: string): boolean {
  return /^\d{11}$/.test(value);
}

// CHP-811: render the Target close date in the wizard review summary using the
// same en-AU DD/MM/YYYY formatter the deal detail / profile sections use,
// rather than the raw ISO string.
function formatCloseDate(value: string | null | undefined): string {
  return value ? new Date(value).toLocaleDateString("en-AU") : "—";
}

// CHP-781: form (DOM) order of the highlightable fields per edit form, so when
// several error at once the FIRST top-to-bottom is the one we scroll/focus and
// name in the summary line.
const CLIENT_FIELD_FOCUS_ORDER = [
  "client_name",
  "client_abn",
  "lead_first_name",
  "client_lead_contact",
  "industry_vertical",
] as const;
const OPPORTUNITY_FIELD_FOCUS_ORDER = [
  "service_offering",
  "primary_use_case",
  "total_contract_value",
  "proposed_term_months",
  "target_close_date",
  "estimated_gpu_count",
] as const;

type Section = "client" | "opportunity" | null;

export function DealWizardStep3({
  deal,
  dealId,
  onSubmit,
  onBack,
  onDealChange,
  stepNumber = 3,
  onFieldError,
  ownerIdentity,
}: Props) {
  const { accessToken } = useCurrentUser();
  const queryClient = useQueryClient();
  const [error, setError] = React.useState<string | null>(null);
  const [editing, setEditing] = React.useState<Section>(null);

  const submit = useMutation<Deal, ApiError, void>({
    mutationFn: () => submitDeal(accessToken!, dealId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["deals"] });
      // CHP-403: no submission toast — the confirmation screen is the focus, so
      // the toast was removed to avoid a redundant duplicate confirmation.
      onSubmit();
    },
    onError: (e) => {
      if (e.status === 409) {
        setError("This deal has already been submitted.");
      } else if (e.status === 422) {
        // CHP-474: a submit 422 means a required field is missing. Decode it so
        // the user learns WHICH field, and — when the field is owned by an
        // earlier step — jump there and highlight it rather than stranding the
        // user on the read-only review summary with no way to act.
        const detail =
          e.body && typeof e.body === "object" && "detail" in e.body
            ? (e.body as { detail?: unknown }).detail
            : undefined;
        const decoded = decode422(detail);
        const targetStep = stepForField(decoded.field);
        if (decoded.field && targetStep && onFieldError) {
          onFieldError(decoded.field);
          return;
        }
        setError(
          decoded.field
            ? decoded.message
            : "Some required fields are missing. Please complete all sections.",
        );
      } else {
        setError("Couldn't submit the deal. Please try again.");
      }
    },
  });

  function handleSubmit() {
    setError(null);
    submit.mutate();
  }

  function handleSaved(updated: Deal) {
    onDealChange(updated);
    setEditing(null);
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Step {stepNumber}: Review &amp; submit</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-6">
        {/* CHP-809 / Flow B "Partner & Owner Identity": for the on-behalf path
            the actor verifies who the deal is registered to before submit. Also
            surfaces a resumed draft's persisted partner org / end-customer
            domain (those fields are no longer collected at registration after
            CHP-339, so a fresh self-registered deal has nothing to show here and
            the block is omitted rather than rendering "—" placeholders). */}
        {ownerIdentity || deal.partner_org_name || deal.client_domain ? (
          <PartnerOwnerSection
            partnerOrgName={ownerIdentity?.orgName ?? deal.partner_org_name}
            clientDomain={deal.client_domain}
            ownerName={ownerIdentity?.ownerName}
            showOwner={!!ownerIdentity}
          />
        ) : null}

        {/* Client details — captured once (the duplicate "Client Info" step
            was removed in this ticket). */}
        {editing === "client" ? (
          <ClientDetailsEditForm
            deal={deal}
            onSaved={handleSaved}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <SummarySection
            title="Client details"
            onEdit={() => {
              setError(null);
              setEditing("client");
            }}
          >
            <SummaryField label="Client name" value={deal.client_name} />
            <SummaryField label="Client ABN" value={deal.client_abn} />
            <SummaryField label="Lead contact" value={deal.client_lead_contact} />
            <SummaryField
              label="Industry vertical"
              value={deal.industry_vertical ? INDUSTRY_LABELS[deal.industry_vertical] : null}
            />
          </SummarySection>
        )}

        {/* Opportunity details */}
        {editing === "opportunity" ? (
          <OpportunityEditForm
            deal={deal}
            onSaved={handleSaved}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <SummarySection
            title="Opportunity details"
            onEdit={() => {
              setError(null);
              setEditing("opportunity");
            }}
          >
            <SummaryField label="Service offering" value={deal.service_offering} />
            <SummaryField label="Primary use case" value={deal.primary_use_case} />
            <SummaryField
              label="Total contract value"
              value={
                deal.total_contract_value != null ? formatAud(deal.total_contract_value) : null
              }
            />
            <SummaryField
              label="Proposed term"
              value={
                deal.proposed_term_months != null ? `${deal.proposed_term_months} months` : null
              }
            />
            <SummaryField
              label="Target close date"
              value={formatCloseDate(deal.target_close_date)}
            />
            <SummaryField
              label="Estimated GPU count"
              value={deal.estimated_gpu_count?.toString() ?? null}
            />
            <SummaryField label="Infrastructure notes" value={deal.infrastructure_notes} />
          </SummarySection>
        )}

        {error ? <FormError>{error}</FormError> : null}

        <div className="flex justify-between">
          <Button type="button" variant="outline" onClick={onBack}>
            Back
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={editing !== null}
            loading={submit.isPending}
            loadingLabel="Submitting…"
          >
            Submit deal registration
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function SummarySection({
  title,
  onEdit,
  children,
}: {
  title: string;
  onEdit: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">{title}</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>
      <dl className="grid gap-2 sm:grid-cols-2">{children}</dl>
    </div>
  );
}

function SummaryField({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="text-sm">{value || "—"}</dd>
    </div>
  );
}

/**
 * CHP-809 / Flow B "Partner & Owner Identity" review block. Read-only: the
 * partner org follows the assignee server-side and the owner was chosen on the
 * Assign-Deal-Owner step, so there's nothing to edit here — it's a verify
 * surface. Only non-empty rows render (the org/domain rows are suppressed when
 * absent, e.g. a self-registered draft that never carried them).
 */
function PartnerOwnerSection({
  partnerOrgName,
  clientDomain,
  ownerName,
  showOwner,
}: {
  partnerOrgName: string | null | undefined;
  clientDomain: string | null | undefined;
  ownerName: string | null | undefined;
  showOwner: boolean;
}) {
  return (
    <div className="rounded-md border p-4">
      <h3 className="mb-3 text-sm font-semibold">Partner &amp; owner</h3>
      <dl className="grid gap-2 sm:grid-cols-2">
        {showOwner ? <SummaryField label="Deal owner" value={ownerName} /> : null}
        {partnerOrgName ? (
          <SummaryField label="Partner organisation" value={partnerOrgName} />
        ) : null}
        {clientDomain ? <SummaryField label="End customer domain" value={clientDomain} /> : null}
      </dl>
    </div>
  );
}

function EditShell({
  title,
  error,
  pending,
  onCancel,
  children,
}: {
  title: string;
  error: string | null;
  pending: boolean;
  onCancel: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="rounded-md border p-4">
      <h3 className="mb-3 text-sm font-semibold">{title}</h3>
      <div className="grid gap-4">
        {children}
        {error ? <FormError>{error}</FormError> : null}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onCancel} disabled={pending}>
            Cancel
          </Button>
          <Button type="submit" size="sm" loading={pending} loadingLabel="Saving...">
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function ClientDetailsEditForm({
  deal,
  onSaved,
  onCancel,
}: {
  deal: Deal;
  onSaved: (deal: Deal) => void;
  onCancel: () => void;
}) {
  const { accessToken } = useCurrentUser();
  // Review-r2 (cad3edb9): Client name is a single field; lead contact splits
  // into First Name + Surname (combined into client_lead_contact on save).
  const [clientName, setClientName] = React.useState(deal.client_name ?? "");
  const [clientAbn, setClientAbn] = React.useState(deal.client_abn ?? "");
  const initialLead = splitName(deal.client_lead_contact);
  const [leadFirstName, setLeadFirstName] = React.useState(initialLead.first);
  const [leadSurname, setLeadSurname] = React.useState(initialLead.surname);
  const [industryVertical, setIndustryVertical] = React.useState<IndustryVertical | "">(
    deal.industry_vertical ?? "",
  );
  const [error, setError] = React.useState<string | null>(null);
  // CHP-781: every invalid field at once, keyed by highlight field name.
  const [errorFields, setErrorFields] = React.useState<Record<string, string>>({});

  const clientNameRef = React.useRef<HTMLInputElement>(null);
  const abnRef = React.useRef<HTMLInputElement>(null);
  const leadFirstNameRef = React.useRef<HTMLInputElement>(null);
  const industryRef = React.useRef<HTMLSelectElement>(null);

  React.useEffect(() => {
    const firstField = CLIENT_FIELD_FOCUS_ORDER.find((f) => f in errorFields);
    if (!firstField) return;
    const el =
      firstField === "client_name"
        ? clientNameRef.current
        : firstField === "client_abn"
          ? abnRef.current
          : firstField === "lead_first_name" || firstField === "client_lead_contact"
            ? leadFirstNameRef.current
            : firstField === "industry_vertical"
              ? industryRef.current
              : null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => el.focus(), 150);
  }, [errorFields]);

  const update = useMutation<Deal, ApiError, DealDraftUpdatePayload>({
    mutationFn: (payload) => updateDealDraft(accessToken!, deal.id, payload),
    onSuccess: onSaved,
    onError: (e) => {
      if (e.status === 422) {
        const detail =
          e.body && typeof e.body === "object" && "detail" in e.body
            ? (e.body as { detail?: unknown }).detail
            : undefined;
        const decoded = decode422(detail);
        setError(decoded.message);
        const hl = highlightFieldFor(decoded.field);
        setErrorFields(hl ? { [hl]: decoded.message } : {});
      } else {
        setErrorFields({});
        setError("Couldn't save changes. Please try again.");
      }
    },
  });

  function onFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setErrorFields({});

    // CHP-781: flag every missing/invalid required field at once.
    const fieldErrors: Record<string, string> = {};

    const trimmedName = clientName.trim();
    if (!trimmedName) {
      fieldErrors.client_name = "Client name is required.";
    }
    const trimmedAbn = clientAbn.trim();
    if (!isValidAbn(trimmedAbn)) {
      fieldErrors.client_abn = "ABN must be exactly 11 digits.";
    }
    const trimmedLeadFirst = leadFirstName.trim();
    if (!trimmedLeadFirst) {
      fieldErrors.lead_first_name = "Lead contact first name is required.";
    }
    if (!industryVertical) {
      fieldErrors.industry_vertical = "Industry vertical is required.";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrorFields(fieldErrors);
      const firstField = CLIENT_FIELD_FOCUS_ORDER.find((f) => f in fieldErrors);
      setError(firstField ? (fieldErrors[firstField] ?? null) : null);
      return;
    }

    // No field errors → industryVertical is set; narrow for the type-checker.
    if (!industryVertical) return;

    const combinedLeadContact = joinName(leadFirstName, leadSurname);

    update.mutate({
      client_name: trimmedName,
      client_abn: trimmedAbn,
      client_lead_contact: combinedLeadContact,
      industry_vertical: industryVertical,
    });
  }

  return (
    <form noValidate onSubmit={onFormSubmit}>
      <EditShell
        title="Client details"
        error={error}
        pending={update.isPending}
        onCancel={onCancel}
      >
        <div className="grid gap-2">
          <Label htmlFor="edit-client-name">Client name *</Label>
          <Input
            id="edit-client-name"
            ref={clientNameRef}
            maxLength={200}
            value={clientName}
            onChange={(e) => setClientName(e.target.value)}
            aria-invalid={"client_name" in errorFields}
            aria-describedby={"client_name" in errorFields ? "edit-client-name-error" : undefined}
          />
          <FormError id="edit-client-name-error" inline>
            {errorFields.client_name}
          </FormError>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="edit-client-abn">Client ABN *</Label>
          <Input
            id="edit-client-abn"
            ref={abnRef}
            maxLength={11}
            inputMode="numeric"
            pattern="\d{11}"
            value={clientAbn}
            onChange={(e) => setClientAbn(e.target.value.replace(/\D/g, ""))}
            aria-invalid={"client_abn" in errorFields}
            aria-describedby={"client_abn" in errorFields ? "edit-client-abn-error" : undefined}
          />
          <FormError id="edit-client-abn-error" inline>
            {errorFields.client_abn}
          </FormError>
        </div>
        <div className="grid gap-2">
          <Label>Lead contact</Label>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label htmlFor="edit-lead-first-name" className="text-xs font-normal">
                First name *
              </Label>
              <Input
                id="edit-lead-first-name"
                ref={leadFirstNameRef}
                maxLength={100}
                value={leadFirstName}
                onChange={(e) => setLeadFirstName(e.target.value)}
                aria-invalid={"lead_first_name" in errorFields}
                aria-describedby={
                  "lead_first_name" in errorFields ? "edit-lead-first-name-error" : undefined
                }
              />
              <FormError id="edit-lead-first-name-error" inline>
                {errorFields.lead_first_name}
              </FormError>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-lead-surname" className="text-xs font-normal">
                Surname
              </Label>
              <Input
                id="edit-lead-surname"
                maxLength={99}
                value={leadSurname}
                onChange={(e) => setLeadSurname(e.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="edit-industry">Industry vertical *</Label>
          <select
            id="edit-industry"
            ref={industryRef}
            value={industryVertical}
            onChange={(e) => setIndustryVertical(e.target.value as IndustryVertical | "")}
            aria-invalid={"industry_vertical" in errorFields}
            aria-describedby={
              "industry_vertical" in errorFields ? "edit-industry-error" : undefined
            }
            className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
          >
            <option value="">-- Select industry --</option>
            {INDUSTRY_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <FormError id="edit-industry-error" inline>
            {errorFields.industry_vertical}
          </FormError>
        </div>
      </EditShell>
    </form>
  );
}

function OpportunityEditForm({
  deal,
  onSaved,
  onCancel,
}: {
  deal: Deal;
  onSaved: (deal: Deal) => void;
  onCancel: () => void;
}) {
  const { accessToken } = useCurrentUser();
  const [serviceOffering, setServiceOffering] = React.useState<ServiceOffering | "">(
    deal.service_offering ?? "BMaaS",
  );
  const [primaryUseCase, setPrimaryUseCase] = React.useState(deal.primary_use_case ?? "");
  const [contractValueDollars, setContractValueDollars] = React.useState(
    centsToDollars(deal.total_contract_value),
  );
  const [termMonths, setTermMonths] = React.useState<string>(
    deal.proposed_term_months?.toString() ?? "",
  );
  const [targetCloseDate, setTargetCloseDate] = React.useState(deal.target_close_date ?? "");
  const [estimatedGpuCount, setEstimatedGpuCount] = React.useState(
    deal.estimated_gpu_count?.toString() ?? "",
  );
  const [infraNotes, setInfraNotes] = React.useState(deal.infrastructure_notes ?? "");
  const [error, setError] = React.useState<string | null>(null);
  // CHP-781: every invalid field at once, keyed by field name.
  const [errorFields, setErrorFields] = React.useState<Record<string, string>>({});

  const serviceRef = React.useRef<HTMLSelectElement>(null);
  const useCaseRef = React.useRef<HTMLTextAreaElement>(null);
  const tcvRef = React.useRef<HTMLInputElement>(null);
  const termRef = React.useRef<HTMLSelectElement>(null);
  const closeDateRef = React.useRef<HTMLInputElement>(null);
  const gpuRef = React.useRef<HTMLInputElement>(null);

  React.useEffect(() => {
    const firstField = OPPORTUNITY_FIELD_FOCUS_ORDER.find((f) => f in errorFields);
    if (!firstField) return;
    const el =
      firstField === "service_offering"
        ? serviceRef.current
        : firstField === "primary_use_case"
          ? useCaseRef.current
          : firstField === "total_contract_value"
            ? tcvRef.current
            : firstField === "proposed_term_months"
              ? termRef.current
              : firstField === "target_close_date"
                ? closeDateRef.current
                : firstField === "estimated_gpu_count"
                  ? gpuRef.current
                  : null;
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    window.setTimeout(() => el.focus(), 150);
  }, [errorFields]);

  const update = useMutation<Deal, ApiError, DealDraftUpdatePayload>({
    mutationFn: (payload) => updateDealDraft(accessToken!, deal.id, payload),
    onSuccess: onSaved,
    onError: (e) => {
      if (e.status === 422) {
        const detail =
          e.body && typeof e.body === "object" && "detail" in e.body
            ? (e.body as { detail?: unknown }).detail
            : undefined;
        const decoded = decode422(detail);
        setError(decoded.message);
        setErrorFields(decoded.field ? { [decoded.field]: decoded.message } : {});
      } else {
        setErrorFields({});
        setError("Couldn't save changes. Please try again.");
      }
    },
  });

  function onFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setErrorFields({});

    // CHP-781: flag every missing/invalid required field at once.
    const fieldErrors: Record<string, string> = {};

    if (!serviceOffering) {
      fieldErrors.service_offering = "Service offering is required.";
    }
    if (!primaryUseCase.trim()) {
      fieldErrors.primary_use_case = "Primary use case is required.";
    }
    const cents = dollarsToCents(contractValueDollars);
    if (cents == null || cents <= 0) {
      fieldErrors.total_contract_value = "Please enter a valid contract value greater than zero.";
    }
    const term = Number(termMonths);
    if (!TERM_OPTIONS.includes(term as (typeof TERM_OPTIONS)[number])) {
      fieldErrors.proposed_term_months =
        "Proposed term must be one of 12, 24, 36, 48 or 60 months.";
    }
    if (!targetCloseDate) {
      fieldErrors.target_close_date = "Target close date is required.";
    } else if (new Date(targetCloseDate) <= new Date()) {
      fieldErrors.target_close_date = "Target close date must be in the future.";
    }

    if (Object.keys(fieldErrors).length > 0) {
      setErrorFields(fieldErrors);
      const firstField = OPPORTUNITY_FIELD_FOCUS_ORDER.find((f) => f in fieldErrors);
      setError(firstField ? (fieldErrors[firstField] ?? null) : null);
      return;
    }

    // No field errors → cents/serviceOffering are valid; narrow for the
    // type-checker.
    if (cents == null || !serviceOffering) return;

    const payload: DealDraftUpdatePayload = {
      service_offering: serviceOffering,
      primary_use_case: primaryUseCase.trim(),
      total_contract_value: cents,
      proposed_term_months: term,
      target_close_date: targetCloseDate,
    };
    const gpu = Number(estimatedGpuCount);
    if (estimatedGpuCount && Number.isFinite(gpu) && gpu > 0) {
      payload.estimated_gpu_count = Math.round(gpu);
    }
    if (infraNotes.trim()) payload.infrastructure_notes = infraNotes.trim();

    update.mutate(payload);
  }

  return (
    <form noValidate onSubmit={onFormSubmit}>
      <EditShell
        title="Opportunity details"
        error={error}
        pending={update.isPending}
        onCancel={onCancel}
      >
        <div className="grid gap-2">
          <Label htmlFor="edit-service">Service offering *</Label>
          <select
            id="edit-service"
            ref={serviceRef}
            value={serviceOffering}
            onChange={(e) => setServiceOffering(e.target.value as ServiceOffering | "")}
            aria-invalid={"service_offering" in errorFields}
            aria-describedby={"service_offering" in errorFields ? "edit-service-error" : undefined}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
          >
            <option value="">-- Select service --</option>
            {SERVICE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <FormError id="edit-service-error" inline>
            {errorFields.service_offering}
          </FormError>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="edit-use-case">Primary use case *</Label>
          <Textarea
            id="edit-use-case"
            ref={useCaseRef}
            rows={3}
            maxLength={2000}
            value={primaryUseCase}
            onChange={(e) => setPrimaryUseCase(e.target.value)}
            aria-invalid={"primary_use_case" in errorFields}
            aria-describedby={"primary_use_case" in errorFields ? "edit-use-case-error" : undefined}
            className="w-full aria-invalid:ring-1"
          />
          <FormError id="edit-use-case-error" inline>
            {errorFields.primary_use_case}
          </FormError>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="edit-tcv">Total contract value (AUD) *</Label>
            <Input
              id="edit-tcv"
              ref={tcvRef}
              type="number"
              min="0"
              step="0.01"
              value={contractValueDollars}
              onChange={(e) => setContractValueDollars(e.target.value)}
              aria-invalid={"total_contract_value" in errorFields}
              aria-describedby={
                "total_contract_value" in errorFields ? "edit-tcv-error" : undefined
              }
            />
            <FormError id="edit-tcv-error" inline>
              {errorFields.total_contract_value}
            </FormError>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-term">Proposed term *</Label>
            <select
              id="edit-term"
              ref={termRef}
              value={termMonths}
              onChange={(e) => setTermMonths(e.target.value)}
              aria-invalid={"proposed_term_months" in errorFields}
              aria-describedby={
                "proposed_term_months" in errorFields ? "edit-term-error" : undefined
              }
              className="h-9 rounded-md border border-input bg-background px-3 text-sm aria-invalid:border-destructive aria-invalid:ring-1 aria-invalid:ring-destructive/20"
            >
              <option value="">-- Select term --</option>
              {TERM_OPTIONS.map((m) => (
                <option key={m} value={m}>
                  {m} months
                </option>
              ))}
            </select>
            <FormError id="edit-term-error" inline>
              {errorFields.proposed_term_months}
            </FormError>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label htmlFor="edit-close-date">Target close date *</Label>
            {/* CHP-396: whole-row click opens the picker. */}
            <DateRowInput
              id="edit-close-date"
              ref={closeDateRef}
              value={targetCloseDate}
              onChange={(e) => setTargetCloseDate(e.target.value)}
              aria-invalid={"target_close_date" in errorFields}
              aria-describedby={
                "target_close_date" in errorFields ? "edit-close-date-error" : undefined
              }
              wrapperClassName={
                "target_close_date" in errorFields
                  ? "border-destructive ring-1 ring-destructive/20"
                  : undefined
              }
            />
            <FormError id="edit-close-date-error" inline>
              {errorFields.target_close_date}
            </FormError>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="edit-gpu">
              Estimated GPU count{" "}
              <span className="font-normal text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="edit-gpu"
              ref={gpuRef}
              type="number"
              min="0"
              step="1"
              value={estimatedGpuCount}
              onChange={(e) => setEstimatedGpuCount(e.target.value)}
              aria-invalid={"estimated_gpu_count" in errorFields}
            />
          </div>
        </div>
        <div className="grid gap-2">
          <Label htmlFor="edit-infra-notes">
            Infrastructure notes{" "}
            <span className="font-normal text-muted-foreground">(optional)</span>
          </Label>
          <Textarea
            id="edit-infra-notes"
            rows={3}
            maxLength={2000}
            value={infraNotes}
            onChange={(e) => setInfraNotes(e.target.value)}
            className="w-full"
          />
        </div>
      </EditShell>
    </form>
  );
}
